import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import TwoFactorSetupModal from '../features/profile/sections/organization/TwoFactorSetupModal';

const TwoFactorEnforcementModal = ({ user, toastStyle, darkMode }) => {
  const [showModal, setShowModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [orgEnforces2FA, setOrgEnforces2FA] = useState(false);
  const [user2FAEnabled, setUser2FAEnabled] = useState(false);

  useEffect(() => {
    checkTwoFactorStatus();
  }, [user]);

  const checkTwoFactorStatus = async () => {
    try {
      if (!user || !user.companyId) {
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      
      // Check if organization enforces 2FA
      const orgResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/organization/2fa-settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check user's 2FA status
      const userResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/2fa/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const orgEnforced = orgResponse.data.success && orgResponse.data.settings.enforced;
      const userEnabled = userResponse.data.success && userResponse.data.status.enabled;
      
      setOrgEnforces2FA(orgEnforced);
      setUser2FAEnabled(userEnabled);
      
      // Show modal if org enforces 2FA but user hasn't set it up
      setShowModal(orgEnforced && !userEnabled);
      
    } catch (error) {
      console.error('Error checking 2FA status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetup2FA = () => {
    setShowSetupModal(true);
  };

  const handleSetupComplete = () => {
    setShowSetupModal(false);
    setShowModal(false);
    setUser2FAEnabled(true);
    // Refresh the page to ensure user can now access the app
    window.location.reload();
  };

  // Don't render anything if loading or not needed
  if (isLoading || !showModal) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white dark:bg-[#181818] rounded-2xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-[#282828]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-100 dark:bg-gray-600/30 p-2 rounded-lg">
                <ShieldCheckIcon className="w-6 h-6 text-black dark:text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Two-Factor Authentication Required
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your organization requires 2FA setup
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4 mb-6">
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-6">
              Your organization administrator has enabled two-factor authentication for all members. 
              You must set up an authenticator app before you can continue using Midus.
            </p>
            
            <div className="bg-gray-50 dark:bg-[#282828] p-4 rounded-xl border border-gray-200 dark:border-[#3c3c3c]">
              <p className="text-gray-800 dark:text-gray-200 text-sm font-medium mb-2">
                What you'll need:
              </p>
              <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
                <li>• An authenticator app (Google Authenticator, Duo, etc.)</li>
                <li>• Your mobile device to scan a QR code</li>
                <li>• About 2 minutes to complete setup</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={handleSetup2FA}
              className="flex-1 bg-black dark:bg-white text-white dark:text-black px-4 py-2.5 rounded-full font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              Set Up Now
            </button>
          </div>
          
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
            This setup is required by your organization and cannot be skipped.
          </p>
        </div>
      </div>

      {/* 2FA Setup Modal */}
      <TwoFactorSetupModal 
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onSetupComplete={handleSetupComplete}
        toastStyle={toastStyle}
        darkMode={darkMode}
      />
    </>
  );
};

export default TwoFactorEnforcementModal; 